import React, { useState, useEffect } from 'react';
import { SPEAKING_IMAGES } from '../constants';
import { X } from 'lucide-react';

const ImageCarousel: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<{ url: string; alt: string } | null>(null);

  // Close modal on escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <>
      <div className="relative overflow-hidden w-full py-4 select-none group/carousel">
        <style>
          {`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-marquee-smooth {
              animation: marquee 35s linear infinite;
              will-change: transform;
            }
            .group\/carousel:hover .animate-marquee-smooth {
              animation-play-state: paused;
            }
            .carousel-paused {
              animation-play-state: paused !important;
            }
          `}
        </style>
        
        <div className={`flex w-max animate-marquee-smooth ${selectedImage ? 'carousel-paused' : ''}`}>
          {/* Track Part 1 */}
          <div className="flex shrink-0 items-center justify-around gap-6 px-3">
            {SPEAKING_IMAGES.map((img, idx) => (
              <div 
                key={`track1-${idx}`} 
                className="relative cursor-pointer group rounded-2xl overflow-hidden aspect-[4/3] w-[220px] md:w-[320px] shadow-sm hover:shadow-xl transition-all duration-500 hover:scale-[1.03]"
                onClick={() => setSelectedImage(img)}
              >
                <img 
                  src={img.url} 
                  alt={img.alt} 
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover pointer-events-none grayscale-[15%] group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
              </div>
            ))}
          </div>

          {/* Track Part 2 (Identical duplicate for seamless infinite loop) */}
          <div aria-hidden="true" className="flex shrink-0 items-center justify-around gap-6 px-3">
            {SPEAKING_IMAGES.map((img, idx) => (
              <div 
                key={`track2-${idx}`} 
                className="relative cursor-pointer group rounded-2xl overflow-hidden aspect-[4/3] w-[220px] md:w-[320px] shadow-sm hover:shadow-xl transition-all duration-500 hover:scale-[1.03]"
                onClick={() => setSelectedImage(img)}
              >
                <img 
                  src={img.url} 
                  alt="" 
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover pointer-events-none grayscale-[15%] group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Minimalist Modal View */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-white/98 backdrop-blur-3xl flex items-center justify-center p-8 md:p-24 cursor-zoom-out animate-in fade-in duration-300"
          onClick={() => setSelectedImage(null)}
        >
          {/* Design-forward Subtle Close Button */}
          <button 
            className="fixed top-8 right-8 md:top-12 md:right-12 p-3 text-gray-300 hover:text-gray-900 transition-all hover:scale-110 active:scale-95 duration-500 z-[110] group"
            onClick={() => setSelectedImage(null)}
            aria-label="Close modal"
          >
            <X size={42} strokeWidth={1} className="transition-transform duration-500 group-hover:rotate-90" />
          </button>
          
          <div className="relative w-full h-full flex items-center justify-center pointer-events-none">
            <img 
              src={selectedImage.url} 
              alt={selectedImage.alt} 
              className="max-w-full max-h-full object-contain rounded-xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)] animate-in zoom-in-95 duration-700 ease-out pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCarousel;